import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import RefreshIcon from '@mui/icons-material/Refresh'
import { toast } from 'react-toastify'
import Header from '../components/Header'
import MenuBar from '../components/MenuBar'
import DedupeWorkspace from '../components/dedupe/DedupeWorkspace'
import httpClient from '../components/helpers/httpClient'
import { updateDuplicates as updateContactDuplicates, setDuplicatesLoading as setContactDuplicatesLoading } from '../store/contactsPage'
import { updateDuplicates as updateCompanyDuplicates, setDuplicatesLoading as setCompanyDuplicatesLoading } from '../store/companiesPage'
import './DuplicateReviewPage.css'

const EMPTY_DUPLICATES = {
  records: [],
  groups: [],
  loading: false,
  lastFetchedAt: null,
}

function DuplicateReviewPage({ entityType }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const isContacts = entityType === 'contact'
  const entityLabel = isContacts ? 'contact' : 'company'
  const entityLabelPlural = isContacts ? 'contacts' : 'companies'
  const routeBase = isContacts ? '/contacts' : '/companies'
  const apiBase = isContacts ? '/api/contacts' : '/api/companies'

  const duplicates = useSelector(state => {
    const pageState = isContacts ? state.contactsPage : state.companiesPage
    return pageState?.duplicates || EMPTY_DUPLICATES
  })

  const updateDuplicates = isContacts ? updateContactDuplicates : updateCompanyDuplicates
  const setDuplicatesLoading = isContacts ? setContactDuplicatesLoading : setCompanyDuplicatesLoading

  const fetchDuplicates = async ({ force = false } = {}) => {
    if (!force && duplicates.lastFetchedAt) {
      return
    }

    dispatch(setDuplicatesLoading(true))
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + `${apiBase}/duplicates`)
      dispatch(updateDuplicates({
        records: Array.isArray(response.data?.records) ? response.data.records : [],
        groups: Array.isArray(response.data?.groups) ? response.data.groups : [],
        lastFetchedAt: Date.now(),
      }))
    } catch (error) {
      toast.error(`Failed to load duplicate ${entityLabelPlural}`)
    } finally {
      dispatch(setDuplicatesLoading(false))
    }
  }

  useEffect(() => {
    fetchDuplicates()
  }, [entityType])

  const handleRefresh = async () => {
    await fetchDuplicates({ force: true })
  }

  const handleMerge = async ({ survivorId, mergedIds }) => {
    await toast.promise(
      httpClient.post(import.meta.env.VITE_API_URL + `${apiBase}/merge`, {
        survivor_id: survivorId,
        merged_ids: mergedIds,
      }),
      {
        pending: `Merging ${entityLabelPlural}...`,
        success: `${entityLabelPlural.charAt(0).toUpperCase() + entityLabelPlural.slice(1)} merged successfully`,
        error: `Failed to merge ${entityLabelPlural}`,
      }
    )
  }

  const handleSelectGroup = (groupId) => {
    const nextParams = new URLSearchParams(searchParams)
    if (groupId) {
      nextParams.set('group', groupId)
    } else {
      nextParams.delete('group')
    }
    setSearchParams(nextParams)
  }

  return (
    <div className='template'>
      <Header>
        <div className='back_button' onClick={() => navigate(routeBase)}><KeyboardBackspaceIcon /> Go Back</div>

        <div className='header__options--right'>
          <button className='refresh-filter-btn duplicate-review-page__refresh-button' onClick={handleRefresh}>
            <RefreshIcon />
          </button>
        </div>
      </Header>

      <MenuBar />

      <div className={`template__body ${searchParams.get('group') ? 'detail-expanded' : ''}`} id='body-duplicate-review'>
        <DedupeWorkspace
          sectionTitle={`Possible ${entityLabel} duplicates`}
          entityLabel={entityLabel}
          entityLabelPlural={entityLabelPlural}
          records={duplicates.records}
          groups={duplicates.groups}
          loading={duplicates.loading}
          onMerge={handleMerge}
          onRefresh={handleRefresh}
          onOpenRecord={(recordId) => navigate(`${routeBase}/${recordId}`)}
          selectedGroupId={searchParams.get('group')}
          onSelectGroup={handleSelectGroup}
          getRecordTitle={(record) => isContacts ? (`${record.first_name || ''} ${record.last_name || ''}`.trim() || `Contact ${record.id}`) : (record.company_name || `Company ${record.id}`)}
          getRecordSubtitle={(record) => isContacts ? (record.email || record.home_email || 'No email') : (record.email || 'No email')}
          getRecordFacts={(record) => isContacts ? [record.city || 'No city', record.mobile_phone || record.business_phone || record.home_phone || `${record.relation_count_total || 0} linked records`] : [record.city || 'No city', record.phone || `${record.relation_count_total || 0} linked records`]}
        />
      </div>
    </div>
  )
}

export default DuplicateReviewPage
